"use client";

import { useState } from "react";
import Link from "next/link";
import { register, verifyOTP, setToken } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, ArrowLeft, Mail } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export function RegisterForm() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [step, setStep] = useState<"form" | "verify">("form");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            await register(email, name);
            setStep("verify");
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Registration failed";
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const { token } = await verifyOTP(email, otpCode);
            setToken(token);
            window.location.href = "/app";
        } catch {
            setError("Invalid or expired code. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (step === "verify") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-4">
                <Card className="w-full max-w-md border-zinc-800 bg-zinc-900 text-zinc-100">
                    <CardHeader>
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-zinc-800">
                            <Mail className="h-6 w-6 text-zinc-400" />
                        </div>
                        <CardTitle className="text-center text-2xl">Check your inbox</CardTitle>
                        <CardDescription className="text-center text-zinc-400">
                            We sent a sign-in code to <span className="text-zinc-200">{email}</span>
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleVerifySubmit} className="space-y-4">
                            {error && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Error</AlertTitle>
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="otp">6-digit code</Label>
                                <Input
                                    id="otp"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                                    required
                                    autoFocus
                                    className="border-zinc-700 bg-zinc-800 text-center text-2xl tracking-[0.5em] text-zinc-100 placeholder:text-zinc-600"
                                />
                            </div>
                            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={loading || otpCode.length !== 6}>
                                {loading ? "Verifying..." : "Verify"}
                            </Button>
                        </form>
                    </CardContent>
                    <CardFooter className="justify-center">
                        <button
                            onClick={() => { setStep("form"); setError(""); setOtpCode(""); }}
                            className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200"
                        >
                            <ArrowLeft className="h-3 w-3" />
                            Back
                        </button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-4">
            <Card className="w-full max-w-md border-zinc-800 bg-zinc-900 text-zinc-100">
                <CardHeader>
                    <CardTitle className="text-2xl">Create account</CardTitle>
                    <CardDescription className="text-zinc-400">
                        Start monitoring your infrastructure in minutes
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleRegisterSubmit} className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="name">Name</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="John Doe"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                autoFocus
                                className="border-zinc-700 bg-zinc-800 text-zinc-100 placeholder:text-zinc-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="border-zinc-700 bg-zinc-800 text-zinc-100 placeholder:text-zinc-500"
                            />
                        </div>
                        <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={loading}>
                            {loading ? "Creating account..." : "Create account"}
                        </Button>
                    </form>
                </CardContent>
                <CardFooter className="justify-center">
                    <p className="text-sm text-zinc-400">
                        Already have an account?{" "}
                        <Link href="/login" className="text-indigo-400 hover:underline">
                            Sign in
                        </Link>
                    </p>
                </CardFooter>
            </Card>
        </div>
    );
}
